import React, {useEffect, useState} from "react";
import {WorkingInformationInterface} from "../../../services/interface/query/working-information-interface";
import {useTranslation} from "react-i18next";
import {PaginationWorkInformation} from "../../../services/interface/query/pagination-work-information";
import CustomTable, {TypeItemTable} from "../../../components/general/CustomTable";
import * as IconMaterial from "@mui/icons-material";
import {formatDateString} from "../../../utils/daysOptions";
import {ItemInfo, ItemNumber, ItemStatusText} from "../../components/ViewData";
import {Person} from "../../../services/interface/query/person";
import {Tooltip} from "@material-tailwind/react";
import IdentificationConsult from "../../terms-conditions/IdentificationConsult";
import {useFindHistoricalWorkInformation, useFindWorkInformation} from "../../../services/apis/apiWorkInformation";
import NotPermissionComponent from "../NotPermissionComponent";
import {getMessageGeneric} from "../../../services/configuration/errorHandle";
import NoRecords from "../../components/NoRecords";
import GraphicMachining from "./GraphicMachining";
import EmploymentHistoryDetails from "./EmploymentHistoryDetails";

interface Props {
    person?: Person;
}

export default function WorkingInformation({person}: Props) {
    const {
        mutate: getWorkInformation,
        data: successPersonWorkInformation,
        error: errorPersonWorkInformation,
        isPending: loadingPersonWorkInformation,
    } = useFindWorkInformation()

    const {
        mutate: getHistoricalWorkInformation,
        data: successPersonHistoricalWork,
        error: errorPersonHistoricalWork,
        isPending: loadingPersonHistoricalWork,
    } = useFindHistoricalWorkInformation()

    const {t} = useTranslation("global") as { t: (key: string) => string };
    const [workInformation, setWorkInformation] = React.useState<WorkingInformationInterface[] | undefined>()
    const [list, setList] = React.useState<PaginationWorkInformation | undefined>()
    const [listElement, setListElement] = useState<TypeItemTable[] | undefined>()
    const [index, setIndex] = useState<number | undefined>()
    const styleIcon = 'min-w-[20px] min-h-[20px]'

    const header = [
        t('emp-ruc'),
        t('emp-name'),
        t('post'),
        t('start-date'),
        t('departure-date'),
    ];

    useEffect(() => {
        if (person && person.identification) {
            getWorkInformation({identifier: person.identification, msj: t('working-information')})
            getHistoricalWorkInformation({identifier: person.identification, page: 0, msj: t('historical')})
        }
    }, [person]);

    useEffect(() => {
        if (successPersonWorkInformation) {
            setWorkInformation(successPersonWorkInformation)
        }
    }, [successPersonWorkInformation]);

    useEffect(() => {
        if (successPersonHistoricalWork) {
            setList(successPersonHistoricalWork)
        }
    }, [successPersonHistoricalWork]);

    useEffect(() => {
        if (list && list.content && list.content.length >= 0) {
            setListElement(list.content.length === 0 ? [] :
                list.content?.map((item: WorkingInformationInterface) => ({
                        row: [
                            <IdentificationConsult
                                className={'text-header pl-1 font-light'}
                                identification={item.taxpayerIdentification}
                            />,
                            item.taxpayerName,
                            item.occupation,
                            item.startDate,
                            item.endDate,
                        ]
                    })
                )
            )
        }
    }, [list]);

    useEffect(() => {
        if ((index || index === 0) && person?.identification) {
            getHistoricalWorkInformation({identifier: person.identification, page: index, msj: t('historical')})
        }
    }, [index]);

    const classNameNumber = 'min-w-[265px] h-[30px]'

    function RenderWorkInformation() {
        if (workInformation && workInformation.length > 0) {
            return (
                <div className={'flex flex-col w-full gap-2'}>
                    {workInformation.map((workItem, index) => (
                        <div key={`work-item-${index}`}
                             className={'mt-1 custom-card py-3 px-3 flex flex-col lg:flex-row gap-4'}>
                            <div className={'flex flex-col gap-4'}>
                                <ItemStatusText value={workItem.employmentStatus}/>
                                <ItemNumber label={t('admission-date')} className={`${classNameNumber} mt-auto`}
                                            value={formatDateString(workItem?.startDate)}/>
                                <ItemNumber label={t('salary')} className={classNameNumber}
                                            value={`$${workItem?.salary}`}/>
                            </div>
                            <div
                                className={'md:w-[100%] lg:w-[70%] flex flex-col gap-6 mt-auto mb-auto justify-center'}>
                                <div className={'flex flex-col lg:flex-row gap-6'}>
                                    <ItemInfo label={t('emp-ruc')} text={workItem?.taxpayerIdentification}
                                              icon={<IconMaterial.FingerprintTwoTone className={styleIcon}/>}/>
                                    <Tooltip className={"bg-header"} content={
                                        <ItemInfo label={t('emp-type')} text={workItem?.taxpayerTypeCompany}
                                                  icon={<IconMaterial.MapsHomeWorkTwoTone className={styleIcon}/>}/>
                                    }>
                                        <div>
                                            <ItemInfo label={t('emp-name')}
                                                      text={workItem?.taxpayerName}
                                                      icon={<IconMaterial.HomeRepairServiceTwoTone
                                                          className={styleIcon}/>}
                                            />
                                        </div>
                                    </Tooltip>

                                </div>
                                <ItemInfo label={t('post')} text={workItem?.occupation}
                                          icon={<IconMaterial.EngineeringTwoTone className={styleIcon}/>}/>
                            </div>
                        </div>))
                    }
                </div>)
        } else {
            return <NoRecords/>
        }
    }

    return (
        <div className={'flex flex-col gap-3'}>
            <GraphicMachining {...{person}}/>
            <EmploymentHistoryDetails {...{person}}/>
            <h3>{t('working-information')}</h3>
            <NotPermissionComponent
                title={'/income/current'}
                loading={loadingPersonWorkInformation}
                error={errorPersonWorkInformation}
                component={<RenderWorkInformation/>}
            />
            <h3>{t('historical')}</h3>
            <CustomTable
                pagination={true}
                error={getMessageGeneric(errorPersonHistoricalWork, '/income/historical')}
                loading={loadingPersonHistoricalWork}
                header={header}
                number={list?.number}
                totalPages={list?.totalPages}
                totalElements={list?.totalElements}
                {...{listElement, setIndex}}
            />
        </div>
    )
}
